/**
 * Accordion expansion state, shared by the container, its items and controls.
 * @module components/ui/Accordion/context
 */

import { createContext, useContext } from "react";

export interface AccordionContextValue {
  /** Set of currently expanded item keys */
  expandedKeys: Set<string>;
  /** Toggle expansion state of an item */
  toggleItem: (key: string) => void;
  /** Expand all items */
  expandAll: () => void;
  /** Collapse all items */
  collapseAll: () => void;
  /** How many items have registered, which is what the bulk toggle is sized by */
  itemCount: number;
  /** How many of the registered items are expanded */
  openCount: number;
  /** Register an item key. Returns the function that takes it back. */
  registerItem: (key: string) => () => void;
}

export const AccordionContext = createContext<AccordionContextValue | null>(null);

/**
 * Hook to access accordion context.
 * Must be used within an Accordion component.
 */
export function useAccordion(): AccordionContextValue {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error("useAccordion must be used within an Accordion component");
  }
  return context;
}
